import type { AnimalDefinition } from './animals/types';

export type PhysicsIsolationPreset =
  | 'baseline'
  | 'gravity-only'
  | 'damping-only'
  | 'moderate-b'
  | 'weight-c'
  | 'settle-c'
  | 'moderate-c';

const BASELINE_FRICTION_AIR = {
  'inverse-meerkat': 0.014,
  'inverse-raccoon': 0.014,
  'inverse-otter': 0.014,
  'inverse-takenoko': 0.014,
  'inverse-koala': 0.014,
} as const;

const MODERATE_B_FRICTION_AIR = {
  'inverse-meerkat': 0.0175,
  'inverse-raccoon': 0.0175,
  'inverse-otter': 0.0175,
  'inverse-takenoko': 0.0175,
  'inverse-koala': 0.0175,
} as const;

const MODERATE_A_FRICTION_AIR = {
  'inverse-meerkat': 0.021,
  'inverse-raccoon': 0.021,
  'inverse-otter': 0.021,
  'inverse-takenoko': 0.021,
  'inverse-koala': 0.021,
} as const;

export const PHYSICS_ISOLATION_PRESETS = {
  baseline: { gravityY: -0.78, frictionAir: BASELINE_FRICTION_AIR },
  'gravity-only': { gravityY: -0.86, frictionAir: BASELINE_FRICTION_AIR },
  'damping-only': { gravityY: -0.78, frictionAir: MODERATE_B_FRICTION_AIR },
  'moderate-b': { gravityY: -0.86, frictionAir: MODERATE_B_FRICTION_AIR },
  'weight-c': { gravityY: -0.94, frictionAir: MODERATE_B_FRICTION_AIR },
  'settle-c': { gravityY: -0.86, frictionAir: MODERATE_A_FRICTION_AIR },
  'moderate-c': { gravityY: -0.92, frictionAir: MODERATE_A_FRICTION_AIR },
} as const satisfies Record<PhysicsIsolationPreset, {
  gravityY: number;
  frictionAir: Record<string, number>;
}>;

const isPhysicsIsolationPreset = (value: string | null): value is PhysicsIsolationPreset => (
  value !== null && Object.hasOwn(PHYSICS_ISOLATION_PRESETS, value)
);

export function resolvePhysicsIsolationPreset(search: string): PhysicsIsolationPreset {
  const params = new URLSearchParams(search);
  const requested = params.get('physicsPreset');
  return params.get('e2e') === '1' && isPhysicsIsolationPreset(requested) ? requested : 'moderate-c';
}

export function activePhysicsIsolationPreset(): PhysicsIsolationPreset {
  return resolvePhysicsIsolationPreset(typeof window === 'undefined' ? '' : window.location.search);
}

export function resolveAnimalPhysics(animal: AnimalDefinition, preset: PhysicsIsolationPreset) {
  const frictionAir = (PHYSICS_ISOLATION_PRESETS[preset].frictionAir as Record<string, number>)[animal.id];
  if (frictionAir === undefined) throw new Error(`Missing physics isolation value for ${animal.id}`);
  return {
    density: animal.density,
    friction: animal.friction,
    frictionStatic: animal.frictionStatic,
    frictionAir,
    restitution: animal.restitution,
  };
}
